/**
 * OTLP LogRecord EMISSION vocabulary: the `LOG_LEVELS` -> severity pair the OTLP spec defines, and the ONE record
 * constructor every emitter builds through. Its live callers are the browser telemetry leg
 * (`const/browser-telemetry-attrs.ts`) and the PostToolUseFailure hook child
 * (`utils/hooks/post-tool-use-failure-telemetry-emit.ts`).
 *
 * HAND-BUILT, ZERO `@opentelemetry/*` PACKAGES, for the reason `const/otlp-span-protocol.ts` states for the TRACE
 * signal (operator decision): the wire shape is the contract, so it is declared rather than imported from a
 * proto-generated bundle. This leaf is the LOGS counterpart of that one and reuses its primitives -- `unixNanoString`
 * for the timestamp, `OtlpSpanAttribute` for the KeyValue shape -- rather than re-deriving either.
 *
 * SEVERITY NUMBERS ARE SPEC VALUES, NOT A LOCAL CHOICE. OTLP partitions `severityNumber` into four-wide bands
 * (TRACE 1-4, DEBUG 5-8, INFO 9-12, WARN 13-16, ERROR 17-20, FATAL 21-24) and a collector's own severity filters key on
 * the NUMBER, so a hand-picked value silently lands a line in the wrong band while `severityText` still reads right.
 * Each level maps to the BASE of its band, which is the conventional single-value-per-band choice.
 *
 * ATTRIBUTE KEYS ARE NOT DECLARED HERE. Every wire key an emitter binds onto a record's KeyValue bag comes from
 * `FEDERA_ATTRS` (`const/telemetry-federa-attrs.ts`), so the bytes have exactly one home and the namespace arm of
 * `QUAL-FEDERA-ATTR-KEY-RAW-LITERAL-001` can answer "is this `federa.*` key registered anywhere at all" from a single
 * record. A per-emitter accessor record in this leaf would put part of the vocabulary beyond that question's reach.
 */

import { LOG_LEVELS } from '@federa/const/logging';
import { unixNanoString } from '@federa/const/otlp-span-protocol';
import type { OtlpSpanAttribute } from '@federa/types/telemetry-otlp-span';
import type { OtlpLogSeverity, TelemetryLogRecord } from '@federa/types/observability-telemetry-log-line';

/** The `LOG_LEVELS` value union. Module-private: it exists to make the map below EXHAUSTIVE, not to be a second home for the level vocabulary. */
type TelemetryLogLevel = (typeof LOG_LEVELS)[keyof typeof LOG_LEVELS];

/**
 * `LOG_LEVELS` member -> OTLP LogRecord severity pair, keyed by the level's WIRE VALUE so a caller holding a
 * `LOG_LEVELS` member indexes it directly with no lookup helper and no fallback arm.
 *
 * `satisfies Record<TelemetryLogLevel, ...>` is what makes a new `LOG_LEVELS` member a COMPILE error here rather than a
 * silent `undefined` at emit time -- the failure a permissive lookup helper would have hidden.
 */
export const OTLP_LOG_SEVERITY = {
	[LOG_LEVELS.DEBUG]: { severityNumber: 5, severityText: 'DEBUG' },
	[LOG_LEVELS.INFO]: { severityNumber: 9, severityText: 'INFO' },
	[LOG_LEVELS.WARN]: { severityNumber: 13, severityText: 'WARN' },
	[LOG_LEVELS.ERROR]: { severityNumber: 17, severityText: 'ERROR' },
} as const satisfies Record<TelemetryLogLevel, OtlpLogSeverity>;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): THE constructor for one OTLP LogRecord this repo emits.
 *
 * ONE place binds the envelope, so no builder can ship a record missing `severityNumber`, carrying a bare-number
 * timestamp, or spelling `body` as a plain string -- the three shapes a collector rejects and a `JSON.stringify`d
 * console line does not.
 *
 * `traceId` is OMITTED rather than emitted empty when the caller has none. An empty or all-zero trace id is INVALID per
 * the spec, and a reader joining on it gets a confident miss instead of the honest absence the omission states.
 */
export function otlpLogRecord(input: {
	timestampMs: number;
	level: TelemetryLogLevel;
	message: string;
	attributes: readonly OtlpSpanAttribute[];
	traceId?: string;
	/** Optional, and only meaningful BESIDE a traceId: OTLP joins a log to a span by the PAIR, so a spanId alone is unresolvable. */
	spanId?: string;
}): TelemetryLogRecord {
	const severity = OTLP_LOG_SEVERITY[input.level];
	const record: TelemetryLogRecord = {
		timeUnixNano: unixNanoString(input.timestampMs),
		severityNumber: severity.severityNumber,
		severityText: severity.severityText,
		body: { stringValue: input.message },
		attributes: [...input.attributes],
	};
	if (input.traceId !== undefined && input.traceId !== '') record.traceId = input.traceId;
	// Gated on the traceId being set, not merely supplied: a spanId on a trace-less record names a span no collector can
	// resolve, which is a dangling pointer rather than a partial join.
	if (record.traceId !== undefined && input.spanId !== undefined && input.spanId !== '') record.spanId = input.spanId;
	return record;
}
